import { Account, AccountType } from '@eagle/core-data-types';
import { Alert, Box, Button, Paper, Stack, styled, Table, TableBody, TableContainer } from '@ethings/ui-toolkit';
import { useTheme } from '@mui/material';
import { FC, useCallback, useEffect, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthenticated } from '../../auth';
import { useFetchAllCache, usePromise, useSmallScreen } from '../../hooks';
import { FindItemsDeferredResult, ListPageQuery } from '../../pages';
import { CacheDataTypes, Undefinable } from '../../types';
import { filterDeletedCache, testid } from '../../util';
import { BasicList } from '../basic-list';
import { FetchOneOfAll } from '../fetch';
import { useStringFlag } from '../flags';
import { MiddleSpinner } from '../middle-spinner';
import { ResponsiveText } from '../responsive-text';
import { TableCell } from '../table-cell';
import { TableRow } from '../table-row';
import { AddAccountDialog } from './add-account-dialog';
import { DeleteSupportAccountDialog } from './delete-support-account-dialog';

export interface Props {
  account: Account;
  disabled?: boolean;
}

const PAGINATION_LIMIT = 5;

interface StyledTableContainerProps {
  smallScreen: boolean;
}

const StyledTableContainer = styled(TableContainer)<StyledTableContainerProps>(({ smallScreen, theme }) => ({
  overflow: smallScreen ? 'auto' : undefined,
  '& + .MuiPagination-root': {
    marginTop: theme.spacing(3),
  },
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(3),
}));

export const SupportAccountManagementCard: FC<Props> = ({ account, disabled }) => {
  const { t } = useTranslation(['common']);
  const theme = useTheme();
  const { userInfo } = useAuthenticated();
  const accountDetailCache = useFetchAllCache(CacheDataTypes.ACCOUNT);
  const myAccountsCache = useFetchAllCache(CacheDataTypes.MY_ACCOUNT);
  const smallScreen = useSmallScreen();
  const nonRemovableAccountIdsString = useStringFlag('portals-support-account-management-non-removable-accountIds');
  const [supportedAccounts, setSupportedAccounts] = useState<Account[]>([]);
  const [supportAccountIds, setSupportAccountIds] = useState<string[]>(account.supportAccountId || []);
  const [addAccountDialogOpen, setAddAccountDialogOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<Undefinable<Account>>(undefined);
  const [myAccounts] = usePromise(async () => {
    const data = await filterDeletedCache<Account>(myAccountsCache);
    return data;
  }, [myAccountsCache]);

  const nonRemovableAccountIds: string[] = nonRemovableAccountIdsString ? nonRemovableAccountIdsString.split(',').map((id) => id.trim()) : [];

  const titleText = t('common:page.account-detail.support-account-management.labels');
  const hintText = t('common:page.account-detail.support-account-management.provision-hint.labels', { accountDisplay: account.display });

  const handleFindAccounts = useCallback(({ pagination }: ListPageQuery): FindItemsDeferredResult<Account> => {
    const limit = pagination?.limit ?? PAGINATION_LIMIT;
    const itemCount = supportedAccounts?.length ?? 0;
    let skip = pagination?.skip ?? 0;

    if (skip >= itemCount && skip > 0) {
      skip = Math.max(0, itemCount - limit);
    }

    const paginatedData = supportedAccounts.slice(skip, skip + limit);
    return {
      cancel: () => { },
      promise: Promise.resolve({
        result: {
          results: paginatedData ?? [],
          itemCount,
        },
        resultDescription: '',
      }),
    };
  }, [supportedAccounts]);

  const handleRemoveSuccess = (): void => {
    if (!selectedAccount) return;
    setSupportAccountIds(supportAccountIds.filter((id) => id !== selectedAccount._id));
    setSelectedAccount(undefined);
    accountDetailCache.invalidate();
  };

  const handleRemoveAccount = (account: Account): void => {
    setSelectedAccount(account);
  };

  const renderList = (items: Undefinable<Account[]>, isLoading: boolean): JSX.Element => {
    if (isLoading || !items || !myAccounts) return <MiddleSpinner />;
    if (!items.length) {
      return <Box my={0}><Alert data-testid="no-search-results" severity="info">{t('common:common.hint.list.no-results')}</Alert></Box>;
    }
    return <StyledTableContainer smallScreen={smallScreen}>
      <Table component="div" data-testid="support-account-management-table" size={smallScreen ? 'medium' : 'small'}>
        <TableBody component="div">
          {items?.map((item, i) => (
            <TableRow key={i} data-testid={testid`support-account-management-table-row-${i}`} sx={{ position: 'relative' }}>
              <TableCell>{item.display}</TableCell>
              <TableCell>
                <FetchOneOfAll
                  dataType={CacheDataTypes.ACCOUNT_TYPE}
                  id={item.accountTypeId}
                  notFoundFactory={() => <span />}
                  renderFactory={(accountType: AccountType) => <>{accountType.display}</>}
                />
              </TableCell>
              <TableCell sx={{ width: 80, height: 44 }}>
                {!disabled && !nonRemovableAccountIds.includes(item._id) && item._id !== userInfo.accountId &&
                  (() => {
                    const RemoveButton = styled(Button)({ color: theme.alerts.red });
                    return (
                      <RemoveButton
                        data-testid={`${i}-remove-button`}
                        disabled={isLoading}
                        onClick={() => handleRemoveAccount(item)}
                      >
                        {t('common:common.action.remove')}
                      </RemoveButton>
                    );
                  })()
                }
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </StyledTableContainer>;
  };

  useEffect(() => {
    if (!myAccounts || !myAccounts.length || !supportAccountIds) return;
    const filteredAccounts = myAccounts.filter((account) => supportAccountIds.includes(account._id));
    setSupportedAccounts(filteredAccounts);
  }, [myAccounts, account, supportAccountIds]);

  const actions = [
    <Stack key="title-link-support-account" direction="row" spacing={2} justifyContent="space-between" px={3}>
      <Stack data-testid="title-support-account-table" flex={[1, 1]}>
        <ResponsiveText
          variant="h5"
          color="text.primary"
          responsiveOptions={{
            minFontSize: '1.25rem',
            maxFontSize: '1.5rem',
            textLengthThreshold: 30,
          }}
        >
          {titleText}
        </ResponsiveText>
        <ResponsiveText
          color="text.secondary"
          variant="body2"
          responsiveOptions={{
            minFontSize: '0.75rem',
            maxFontSize: '0.875rem',
            textLengthThreshold: 50,
          }}
        >
          {hintText}
        </ResponsiveText>
      </Stack>
      {(() => {
        const LinkSupportButton = styled(Button)({ alignSelf: 'flex-start' });
        return (
          <LinkSupportButton data-testid="link-support-account-button" onClick={() => setAddAccountDialogOpen(true)} disabled={disabled}>
            {t('common:page.account-detail.support-account-management.link-support-account.action')}
          </LinkSupportButton>
        );
      })()}
    </Stack>,
  ];

  return (
    <StyledPaper data-testid='account-detail-support-account'>
      <BasicList
        actions={actions}
        limit={PAGINATION_LIMIT}
        onQueryChanged={handleFindAccounts}
        renderContent={renderList}
      />
      {selectedAccount &&
        <DeleteSupportAccountDialog
          handleClose={() => setSelectedAccount(undefined)}
          open={!!selectedAccount}
          account={account}
          accountToRemove={selectedAccount}
          supportAccountIds={supportAccountIds}
          handleRemoveSuccess={handleRemoveSuccess}
        />
      }
      <AddAccountDialog
        key={account._id}
        data={account}
        entity="account"
        handleClose={() => setAddAccountDialogOpen(false)}
        open={addAccountDialogOpen}
        setSupportedAccounts={setSupportedAccounts}
        supportedAccounts={supportedAccounts}
      />
    </StyledPaper>
  );
};
